import React, { useEffect, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import { isScheduleJobDone, useBackgroundTasksStatus } from '../../app/BackgroundTasksStatusProvider';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import {
  GetQueuesComponent,
  UpdateScheduleInfoComponent,
  CreateScheduleComponent,
  UpdateScheduleComponent,
  GetSchedulesComponent,
  GetScheduleComponent,
  LockType,
} from '../../graphql/components';
import { ActionRouteType, CreateEditView, IdRouteType } from '../../types';
import { Blank } from '../Blank/Blank';
import { NotFound } from '../NotFound/NotFound';
import CreateScheduleScene from './CreateScheduleScene';
import { LocationState } from './types';
import { getPathname } from './utils/pathname';

type CreateScheduleSceneContainerProps = RouteComponentProps<ActionRouteType & Partial<IdRouteType>, {}, LocationState>;

export const CreateScheduleSceneContainer: React.FC<CreateScheduleSceneContainerProps> = ({ location, match }) => {
  const { action, id = '' } = match.params;
  const { state } = location;
  const isCreate = action === CreateEditView.CREATE;
  const isEdit = action === CreateEditView.EDIT;
  const isView = action === CreateEditView.VIEW;

  const {
    autoScheduler: { schedulesStates, removeScheduleFromWatch },
  } = useBackgroundTasksStatus();

  const [shouldSwitchToEdit, setShouldSwitchToEdit] = useState(false);
  const [lockType, setLockType] = useState<LockType | null>(state?.scheduleState?.schedule?.info?.lockType ?? null);

  const shouldGetSchedule = !isCreate && !(state && (state.initialScheduleInfo || state.oldFormValues));

  useEffect(() => {
    if (id && schedulesStates[id] && isScheduleJobDone(schedulesStates[id].state)) {
      if (isView) {
        setShouldSwitchToEdit(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id && schedulesStates[id], id]);

  return (
    <GetScheduleComponent variables={{ id }} skip={!shouldGetSchedule} fetchPolicy="network-only">
      {({ loading, error, data, client }) => (
        <LoadingOverlay loading={loading}>
          {(() => {
            if (loading || error) {
              return <Blank />;
            }

            const scheduleInfo = data && data.schedule ? data.schedule.info : undefined;

            if (lockType === null && scheduleInfo?.lockType) {
              // Update latest lockType value based on the latest BE data
              setLockType(scheduleInfo.lockType);
            }

            // Redirect user to edit mode triggered when auto-schedule job finished while visiting the view mode
            if (shouldSwitchToEdit) {
              setShouldSwitchToEdit(false);
              setLockType(LockType.None);
              removeScheduleFromWatch(id);

              return (
                <Redirect
                  to={{ pathname: getPathname(CreateEditView.EDIT, id), state: { forceRefetchSchedule: true } }}
                />
              );
            }

            // Block forced edit access via URL when schedule is being calculated in autoSchedule thus not available for edit
            if (lockType === LockType.Write && isEdit) {
              return <Redirect to={getPathname(CreateEditView.VIEW, id)} />;
            }

            const initialScheduleInfo = (() => {
              if (isCreate) {
                // Coming from Forecast Studio
                if (state && state.initialScheduleInfo) {
                  return state.initialScheduleInfo;
                }

                return undefined;
              }

              if (shouldGetSchedule) {
                return scheduleInfo;
              }

              return state.initialScheduleInfo;
            })();

            return shouldGetSchedule && !scheduleInfo ? (
              <NotFound />
            ) : (
              <CreateScheduleComponent>
                {(createSchedule, { loading: isCreateScheduleInProgress }) => (
                  <UpdateScheduleComponent>
                    {(updateSchedule, { loading: isUpdateScheduleInProgress }) => (
                      <UpdateScheduleInfoComponent>
                        {(updateScheduleInfo, { loading: isUpdateScheduleInfoInProgress }) => (
                          <GetQueuesComponent>
                            {(queuesResult) => (
                              <GetSchedulesComponent skip={!isCreate} fetchPolicy="network-only">
                                {(schedulesResult) => (
                                  <LoadingOverlay
                                    loading={
                                      isCreateScheduleInProgress ||
                                      isUpdateScheduleInProgress ||
                                      isUpdateScheduleInfoInProgress ||
                                      queuesResult.loading
                                    }
                                  >
                                    <CreateScheduleScene
                                      schedulesStates={schedulesStates}
                                      createSchedule={createSchedule}
                                      updateSchedule={updateSchedule}
                                      updateScheduleInfo={updateScheduleInfo}
                                      queuesResult={queuesResult}
                                      schedulesResult={schedulesResult}
                                      initialScheduleInfo={initialScheduleInfo}
                                      client={client}
                                    />
                                  </LoadingOverlay>
                                )}
                              </GetSchedulesComponent>
                            )}
                          </GetQueuesComponent>
                        )}
                      </UpdateScheduleInfoComponent>
                    )}
                  </UpdateScheduleComponent>
                )}
              </CreateScheduleComponent>
            );
          })()}
        </LoadingOverlay>
      )}
    </GetScheduleComponent>
  );
};
